import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import DashboardView from '../views/DashboardView';
import ProjectsView from '../views/ProjectsView';
import InventoryView from '../views/InventoryView';
import SettingsView from '../views/SettingsView';
import DemoView from '../views/DemoView';

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: 'Grid' },
  { id: 'projects', label: 'Projects', icon: 'Layout' },
  { id: 'inventory', label: 'Materials', icon: 'Box' },
  { id: 'demo', label: 'Demo', icon: 'Zap' },
  { id: 'settings', label: 'System', icon: 'Settings' },
];

export default function Layout() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return <DashboardView onNavigate={setActiveTab} />;
      case 'projects': return <ProjectsView />;
      case 'inventory': return <InventoryView />;
      case 'demo': return <DemoView />;
      case 'settings': return <SettingsView />;
      default: return <DashboardView />;
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-graphite-900 bg-grid-pattern relative">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-64 glass-panel border-r border-graphite-700/50 z-10 relative">
        <div className="p-6 flex items-center gap-3">
          <div className="w-8 h-8 rounded bg-maker-cyan/20 border border-maker-cyan flex items-center justify-center text-maker-cyan glow-cyan">
            <SafeIcon name="Cpu" className="w-5 h-5" />
          </div>
          <span className="text-xl font-bold tracking-wider text-white text-glow">CRAFT<span className="text-maker-cyan">CLOUD</span></span>
        </div>
        
        <nav className="flex-1 px-4 py-6 space-y-2">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 relative ${
                activeTab === item.id 
                  ? 'bg-maker-cyan/10 text-maker-cyan border border-maker-cyan/30 glow-cyan' 
                  : 'text-gray-400 hover:bg-graphite-700 hover:text-gray-200'
              }`}
            >
              <SafeIcon name={item.icon} className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
              {activeTab === item.id && (
                <motion.div layoutId="activeNav" className="absolute left-0 w-1 h-8 bg-maker-cyan rounded-r-full" />
              )}
            </button>
          ))}
        </nav>
      </aside>

      {/* Mobile Topbar & Nav */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 glass-panel z-20 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
           <div className="w-6 h-6 rounded bg-maker-cyan/20 border border-maker-cyan flex items-center justify-center text-maker-cyan">
            <SafeIcon name="Cpu" className="w-4 h-4" />
          </div>
          <span className="font-bold text-white">CRAFT<span className="text-maker-cyan">CLOUD</span></span>
        </div>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-gray-300 p-2">
          <SafeIcon name={isMobileMenuOpen ? "X" : "Menu"} className="w-6 h-6" />
        </button>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden fixed top-16 left-0 right-0 glass-panel border-b border-graphite-700 z-30 flex flex-col p-4 space-y-2"
          >
             {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setIsMobileMenuOpen(false); }}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                  activeTab === item.id ? 'bg-maker-cyan/20 text-maker-cyan' : 'text-gray-400'
                }`}
              >
                <SafeIcon name={item.icon} className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content Area */}
      <main className="flex-1 h-full overflow-y-auto pt-16 md:pt-0 relative">
        <div className="absolute top-0 left-1/4 w-1/2 h-32 bg-maker-cyan/5 blur-[100px] pointer-events-none" />
        
        <div className="p-4 md:p-8 max-w-7xl mx-auto min-h-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="h-full"
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}